/**
 * VERA-260 — the company-level reporting currency for analytics money metrics.
 *
 * ONE source, read by BOTH the API's update validator (`UpdateCompanyPayloadDto`'s
 * `@IsIn`) and the platform's Settings → Company currency picker. Adding a code
 * here is the single edit that makes it acceptable on both surfaces — the DB
 * CHECK constraint (`companies_reporting_currency_iso4217`, migration
 * `0096_companies_reporting_currency.sql`) only enforces SHAPE (`^[A-Z]{3}$`),
 * not membership, so this list is the real allowlist.
 *
 * Deliberately a CURATED set of ~20 major trading currencies, not the full
 * ISO-4217 table (~180 codes). A dropdown of every currency on earth, most of
 * which no brand on this platform will ever hold a Stripe balance in, is worse
 * UX than a short list a person can actually scan — and every entry here is one
 * the API will accept, so the list never advertises a choice that then 400s.
 *
 * Analytics money metrics count only rows whose currency matches the company's
 * reporting currency. Nothing is ever FX-converted — a resale priced in a
 * different currency is excluded from those figures, not converted into this
 * one (working-conventions §67 / the ROI card's own caveat).
 */
export interface IReportingCurrency {
  /** ISO-4217 alpha-3 code, uppercase. */
  code: string;
  /** Human-readable currency name, e.g. "US Dollar". */
  label: string;
  /** Display symbol, e.g. "$". */
  symbol: string;
}

export const REPORTING_CURRENCIES: readonly IReportingCurrency[] = [
  {code: 'USD', label: 'US Dollar', symbol: '$'},
  {code: 'EUR', label: 'Euro', symbol: '€'},
  {code: 'GBP', label: 'British Pound', symbol: '£'},
  {code: 'JPY', label: 'Japanese Yen', symbol: '¥'},
  {code: 'CAD', label: 'Canadian Dollar', symbol: 'CA$'},
  {code: 'AUD', label: 'Australian Dollar', symbol: 'A$'},
  {code: 'CHF', label: 'Swiss Franc', symbol: 'CHF'},
  {code: 'CNY', label: 'Chinese Yuan', symbol: '¥'},
  {code: 'HKD', label: 'Hong Kong Dollar', symbol: 'HK$'},
  {code: 'SGD', label: 'Singapore Dollar', symbol: 'S$'},
  {code: 'SEK', label: 'Swedish Krona', symbol: 'kr'},
  {code: 'NOK', label: 'Norwegian Krone', symbol: 'kr'},
  {code: 'DKK', label: 'Danish Krone', symbol: 'kr'},
  {code: 'NZD', label: 'New Zealand Dollar', symbol: 'NZ$'},
  {code: 'KRW', label: 'South Korean Won', symbol: '₩'},
  {code: 'INR', label: 'Indian Rupee', symbol: '₹'},
  {code: 'MXN', label: 'Mexican Peso', symbol: 'MX$'},
  {code: 'BRL', label: 'Brazilian Real', symbol: 'R$'},
  {code: 'ZAR', label: 'South African Rand', symbol: 'R'},
  {code: 'AED', label: 'UAE Dirham', symbol: 'AED'},
] as const;

export const REPORTING_CURRENCY_CODES: readonly string[] = REPORTING_CURRENCIES.map(
  (c) => c.code,
);

export const DEFAULT_REPORTING_CURRENCY = 'USD';

export function isReportingCurrency(v: unknown): v is string {
  return typeof v === 'string' && REPORTING_CURRENCY_CODES.includes(v.toUpperCase());
}
